import { Link } from "@tanstack/react-router";
import clsx from "clsx";
import noImage from "@/assets/img/no-image.svg";
import {
  getImageUrl,
  getThreadTitle,
  type ThreadListItem,
} from "@/entities/thread";
import { useFilteredThreads } from "@/features/catalog/hooks/useFilteredThreads";
import { useThreads } from "@/features/catalog/hooks/useThreads";
import { useHistoryStore } from "@/features/history/stores/historyStore";
import { useCurrentBoardSlug } from "@/shared/hooks/useCurrentBoardSlug";
import { decorateTitle } from "@/shared/lib";
import { LoadingScreen, Message } from "@/shared/ui/feedback";

/**
 * 板の List view (catalog の代替表示モード、PC のみ)。
 * 1 カラム縦並びでサムネイル小 + タイトル + レス数を表示。
 *
 * Figma `Page : Top /Desktop and Tablet` 相当。既存 viewer に list 表示は
 * 無く、「既存 UI 再現原則」の唯一の例外として user 承認済みで新規実装。
 */
export const CatalogListView: React.FunctionComponent = () => {
  const { data, isLoading, error } = useThreads();
  const visibleThreads = useFilteredThreads(data?.items);
  const boardSlug = useCurrentBoardSlug();
  const { addViewed, getViewedIds } = useHistoryStore();
  const viewedIds = new Set(getViewedIds());

  if (isLoading) return <LoadingScreen />;

  if (error) {
    return (
      <Message variant="error">スレッド一覧の読み込みに失敗しました</Message>
    );
  }

  if (visibleThreads.length === 0 && data?.items && data.items.length > 0) {
    return (
      <Message variant="info">
        検索条件に一致するスレッドが見つかりません
      </Message>
    );
  }

  return (
    <ul className="divide-y divide-border max-w-3xl mx-auto">
      {visibleThreads.map((thread: ThreadListItem) => {
        const title = decorateTitle(getThreadTitle(thread));
        const isViewed = viewedIds.has(thread.id);
        return (
          <li key={thread.id}>
            <Link
              to="/$boardSlug/thread/$threadId"
              params={{ boardSlug, threadId: thread.id }}
              onClick={(): void => addViewed(thread.id, boardSlug)}
              className={clsx(
                "flex gap-3 p-3 hover:bg-muted transition-colors",
                isViewed && "bg-muted/30",
              )}
            >
              <img
                src={getImageUrl(thread.firstAttachment, false)}
                alt=""
                loading="lazy"
                onError={(e: React.SyntheticEvent<HTMLImageElement>): void => {
                  (e.target as HTMLImageElement).src = noImage;
                }}
                className="w-20 h-20 rounded object-cover bg-muted flex-none"
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-foreground line-clamp-2">
                  {title}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {thread.postCount}レス
                </div>
              </div>
            </Link>
          </li>
        );
      })}
    </ul>
  );
};
